import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMenuScheduling } from '../../context/menuSchedulingContext';
import { useAuth } from '../../context/authContext';
import { getUserRecipes, getAllStackEntries, createOccasionFromRecipes } from '../../services/api';
import SignedImage from '../SignedImage';
import OccasionNamePrompt from '../OccasionNamePrompt';
import '../../styles/menuSchedulingPopup.css';

const MenuSchedulingPopup = () => {
    const { 
        showScheduleModal, 
        closeScheduleModal, 
        selectedRecipeIds,
        selectedRecipes,
        recipeOrder,
        updateRecipeOrder,
        toggleRecipeSelection,
        addRecipeToMap,
        clearSelection,
        currentView,
        setSchedule,
        backToSelection,
        scheduledPlan
    } = useMenuScheduling();
    
    const { user } = useAuth();
    const navigate = useNavigate();
    const [scheduling, setScheduling] = useState(false);
    const [error, setError] = useState(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [draggedRecipeId, setDraggedRecipeId] = useState(null);
    const [dragOverRecipeId, setDragOverRecipeId] = useState(null);
    const [showNamePrompt, setShowNamePrompt] = useState(false);
    const [occasionId, setOccasionId] = useState(null);
    const popupRef = useRef(null);

    // Get selected recipes ordered by recipeOrder
    const selectedRecipesData = React.useMemo(() => {
        const recipesArray = Array.from(selectedRecipes.values());
        if (recipeOrder.length === 0) {
            return recipesArray;
        }
        // Sort by order, with unordered items at the end
        return recipesArray.sort((a, b) => {
            const aIndex = recipeOrder.indexOf(a.id);
            const bIndex = recipeOrder.indexOf(b.id);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [selectedRecipes, recipeOrder]);
    
    // Fallback: Load missing recipes if we have IDs but not objects
    useEffect(() => {
        const loadMissingRecipes = async () => {
            const missingIds = Array.from(selectedRecipeIds).filter(id => !selectedRecipes.has(id));
            if (missingIds.length === 0) {
                return;
            }

            try {
                const allRecipes = [];
                
                // Load user recipes
                if (user) {
                    const userRecipes = await getUserRecipes();
                    allRecipes.push(...userRecipes);
                }
                
                // Load explore recipes
                const exploreRecipes = await getAllStackEntries();
                allRecipes.push(...exploreRecipes);

                // Add missing recipes to context
                for (const id of missingIds) {
                    const recipe = allRecipes.find(r => r.id === id);
                    if (recipe) {
                        addRecipeToMap(id, recipe);
                    }
                }
            } catch (err) {
                console.error('Error loading missing recipes:', err);
            }
        };

        loadMissingRecipes();
    }, [selectedRecipeIds, selectedRecipes, user, addRecipeToMap]);

    // Drag handlers
    const handleMouseDown = (e) => {
        // Don't start dragging if clicking on interactive elements
        if (e.target.closest('.menu-scheduling-popup-close') || 
            e.target.closest('.menu-scheduling-button') ||
            e.target.closest('.menu-scheduling-remove-button') ||
            e.target.closest('button') ||
            e.target.closest('a')) {
            return;
        }
        
        // Only allow dragging from header area
        if (!e.target.closest('.menu-scheduling-popup-header')) {
            return;
        }
        
        setIsDragging(true);
        const rect = popupRef.current?.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Constrain to viewport
            const maxX = window.innerWidth - (popupRef.current?.offsetWidth || 400);
            const maxY = window.innerHeight - (popupRef.current?.offsetHeight || 200);
            
            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const handleCancel = () => {
        clearSelection();
        closeScheduleModal();
    };

    const handleSchedule = () => {
        if (selectedRecipeIds.size < 2) {
            return;
        }
        // Show name prompt first
        setShowNamePrompt(true);
    };

    const handleNamePromptContinue = async (occasionName) => {
        setShowNamePrompt(false);
        
        try {
            setScheduling(true);
            setError(null);

            // Get Firebase token
            const token = await user.getIdToken();

            // Use recipeOrder if available, otherwise use selectedRecipeIds order
            const orderedRecipeIds = recipeOrder.length > 0 
                ? recipeOrder.filter(id => selectedRecipeIds.has(id))
                : Array.from(selectedRecipeIds);

            // Create occasion first
            const servingsMap = {}; // Default servings for menu planning
            orderedRecipeIds.forEach(id => {
                servingsMap[id] = 4; // Default 4 servings
            });

            const occasionResponse = await createOccasionFromRecipes(
                orderedRecipeIds,
                servingsMap,
                occasionName,
                'menu_plan',
                token
            );

            const newOccasionId = occasionResponse.occasion.id;
            setOccasionId(newOccasionId);

            // Call backend scheduling API
            const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const response = await fetch(`${apiBaseUrl}/menu-planning/schedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    recipe_ids: orderedRecipeIds,
                    options: {
                        priority: 'recipeOrder',
                        recipe_order: orderedRecipeIds
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate schedule');
            }

            const scheduleData = await response.json();

            const plan = {
                recipes: selectedRecipesData,
                segments: scheduleData.segments,
                makespanSec: scheduleData.makespanSec,
                activeTotalSec: scheduleData.activeTotalSec,
                inactiveTotalSec: scheduleData.inactiveTotalSec
            };
            
            setSchedule(plan);
            
            // Save menu plan automatically with occasion_id
            try {
                const planName = occasionName;
                const recipeIds = orderedRecipeIds;
                
                const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
                const saveResponse = await fetch(`${apiBaseUrl}/menu-planning/plans`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        name: planName,
                        recipe_ids: recipeIds,
                        schedule_data: plan,
                        occasion_id: newOccasionId
                    })
                });
                
                if (saveResponse.ok) {
                    const savedPlan = await saveResponse.json();
                    // Navigate to schedule page with saved plan ID and occasion ID
                    navigate('/schedule', { state: { schedule: plan, menuPlanId: savedPlan.id, occasionId: newOccasionId } });
                } else {
                    // Still navigate even if save fails
                    navigate('/schedule', { state: { schedule: plan, occasionId: newOccasionId } });
                }
            } catch (saveErr) {
                console.warn('Failed to save menu plan:', saveErr);
                // Still navigate even if save fails
                navigate('/schedule', { state: { schedule: plan, occasionId: newOccasionId } });
            }
            
            closeScheduleModal();
        } catch (err) {
            console.error('Error scheduling recipes:', err);
            setError(err.message || 'Failed to generate schedule');
        } finally {
            setScheduling(false);
        }
    };

    const handleRemoveRecipe = (recipeId) => {
        toggleRecipeSelection(recipeId);
    };

    const handlePrintPlan = () => {
        // Open printable plan in new window
        const printWindow = window.open('', '_blank', 'width=1000,height=800');
        if (printWindow && scheduledPlan) {
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Cooking Plan - ${scheduledPlan.recipes.map(r => r.name).join(', ')}</title>
                    <style>
                        body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
                        #root { width: 100%; min-height: 100vh; }
                    </style>
                </head>
                <body>
                    <div id="root"></div>
                </body>
                </html>
            `);
            printWindow.document.close();

            // Wait for window to load, then render React component
            setTimeout(() => {
                const { createRoot } = require('react-dom/client');
                const React = require('react');
                const PrintableCookingPlan = require('./PrintableCookingPlan').default;
                const root = createRoot(printWindow.document.getElementById('root'));
                root.render(
                    React.createElement(PrintableCookingPlan, {
                        scheduledPlan: scheduledPlan,
                        recipes: scheduledPlan.recipes
                    })
                );
            }, 100);
        }
    };

    const handleStartSession = async () => {
        try {
            const token = await user.getIdToken();
            
            const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const response = await fetch(`${apiBaseUrl}/sessions/menu-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    menu_plan: {
                        recipes: scheduledPlan.recipes.map(r => ({ id: r.id, name: r.name })),
                        segments: scheduledPlan.segments,
                        makespanSec: scheduledPlan.makespanSec
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create menu session');
            }

            const sessionData = await response.json();
            window.location.href = `/live-session/${sessionData.sessionId}?type=menu`;
            closeScheduleModal();
        } catch (err) {
            console.error('Error creating menu session:', err);
            setError(err.message || 'Failed to start session');
        }
    };

    if (!showScheduleModal) {
        return null;
    }

    return (
        <>
            <OccasionNamePrompt
                isOpen={showNamePrompt}
                onClose={() => setShowNamePrompt(false)}
                onContinue={handleNamePromptContinue}
                title="Name Your Menu Plan"
            />
            {!showNamePrompt && createPortal(
        <div 
            className="menu-scheduling-popup"
            ref={popupRef}
            style={{
                position: 'fixed',
                ...(position.x === 0 && position.y === 0 
                    ? { bottom: '24px', right: '24px' }
                    : { left: `${position.x}px`, top: `${position.y}px` }
                ),
                cursor: isDragging ? 'grabbing' : 'default'
            }}
        >
            <div className="menu-scheduling-popup-content">
                {/* Header - Draggable */}
                <div 
                    className="menu-scheduling-popup-header"
                    onMouseDown={handleMouseDown}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div className="menu-scheduling-popup-header-top">
                        <div className="menu-scheduling-popup-title">
                            <span className="font-mono text-sm uppercase tracking-wider">
                                Menu Scheduling
                            </span>
                            <span className="menu-scheduling-popup-count">
                                {selectedRecipeIds.size} {selectedRecipeIds.size === 1 ? 'recipe' : 'recipes'} selected
                            </span>
                        </div>
                        
                        <button
                            className="menu-scheduling-popup-close"
                            onClick={handleCancel}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="menu-scheduling-popup-body">
                    <div className="menu-scheduling-selected-recipes">
                        {selectedRecipesData.length === 0 ? (
                            <div className="menu-scheduling-empty-state">
                                <div className="menu-scheduling-empty-text">
                                    Select recipes from the Explore or My Recipes pages
                                </div>
                                <div className="menu-scheduling-empty-hint">
                                    Click recipe cards to add them to your menu plan
                                </div>
                            </div>
                        ) : (
                            <div className="menu-scheduling-recipe-cards">
                                {selectedRecipesData.map((recipe, index) => {
                                    const isDragging = draggedRecipeId === recipe.id;
                                    const isDragOver = dragOverRecipeId === recipe.id;
                                    
                                    return (
                                        <div 
                                            key={recipe.id} 
                                            className={`menu-scheduling-recipe-card ${isDragging ? 'menu-scheduling-recipe-card-dragging' : ''} ${isDragOver ? 'menu-scheduling-recipe-card-drag-over' : ''}`}
                                            draggable
                                            onDragStart={(e) => {
                                                setDraggedRecipeId(recipe.id);
                                                e.dataTransfer.effectAllowed = 'move';
                                            }}
                                            onDragEnd={() => {
                                                setDraggedRecipeId(null);
                                                setDragOverRecipeId(null);
                                            }}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                                if (dragOverRecipeId !== recipe.id) {
                                                    setDragOverRecipeId(recipe.id);
                                                }
                                            }}
                                            onDragLeave={() => {
                                                setDragOverRecipeId(null);
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                if (draggedRecipeId && draggedRecipeId !== recipe.id) {
                                                    const currentOrder = recipeOrder.length > 0 
                                                        ? [...recipeOrder.filter(id => selectedRecipeIds.has(id))]
                                                        : selectedRecipesData.map(r => r.id);
                                                    
                                                    const draggedIndex = currentOrder.indexOf(draggedRecipeId);
                                                    const dropIndex = currentOrder.indexOf(recipe.id);
                                                    
                                                    const newOrder = [...currentOrder];
                                                    newOrder.splice(draggedIndex, 1);
                                                    newOrder.splice(dropIndex, 0, draggedRecipeId);
                                                    
                                                    updateRecipeOrder(newOrder);
                                                }
                                                setDraggedRecipeId(null);
                                                setDragOverRecipeId(null);
                                            }}
                                        >
                                            <div className="menu-scheduling-recipe-card-drag-handle">
                                                ⋮⋮
                                            </div>
                                            <div className="menu-scheduling-recipe-card-thumbnail">
                                                <SignedImage
                                                    src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                                    alt={recipe.name}
                                                    className="menu-scheduling-recipe-card-image"
                                                    category={recipe.category}
                                                />
                                            </div>
                                            <div className="menu-scheduling-recipe-card-info">
                                                <div className="menu-scheduling-recipe-card-name">{recipe.name}</div>
                                                {recipe.category && (
                                                    <div className="menu-scheduling-recipe-card-category">{recipe.category}</div>
                                                )}
                                                {recipe.cooking_time && (
                                                    <div className="menu-scheduling-recipe-card-time">
                                                        {recipe.cooking_time} min
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="menu-scheduling-recipe-card-remove"
                                                onClick={() => handleRemoveRecipe(recipe.id)}
                                                aria-label="Remove recipe"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="menu-scheduling-popup-footer">
                    <button
                        className="menu-scheduling-button menu-scheduling-button-secondary"
                        onClick={handleCancel}
                    >
                        CANCEL
                    </button>
                    <button
                        className="menu-scheduling-button menu-scheduling-button-primary"
                        onClick={handleSchedule}
                        disabled={selectedRecipeIds.size < 2 || scheduling}
                    >
                        {scheduling ? 'SCHEDULING...' : `SCHEDULE ${selectedRecipeIds.size >= 2 ? `(${selectedRecipeIds.size})` : ''}`}
                    </button>
                </div>

                {error && (
                    <div className="menu-scheduling-error">
                        {error}
                    </div>
                )}
            </div>
        </div>
            , document.body)}
        </>
    );
};

export default MenuSchedulingPopup;

